// Ops-board chip colour channels (PR-000058 / PR-000059, Ben's scheme settled 2026-09-03/04).
//
// THE SCHEME. The chip carries three independent colour channels, so status, type and person
// stop competing for one fill:
//
//   LEFT BAR  confirmation status   already shipped — DayView `statusStripe`. NOT touched here.
//   TOP BAR   booking type          new; the type moves OFF the fill and onto its own bar.
//   FILL      the instructor        new; the fill, vacated by the type, now says WHO IS FLYING.
//
// ⚠ THE HANDOVER HAS HAPPENED (0.12.4). The two channels were a constant on purpose while
// `CompanySettings` had no write path; 0.12.3 shipped the columns, the admin screen and the
// PATCH, and this module now READS them — `resolveChipChannels` below is the one place the
// settings value is turned into a `ChipChannelSource`. CHIP_CHANNELS remains, demoted to the
// FALLBACK for a client with no settings (offline cache miss) or a row that cannot be trusted.
// It is no longer "the thing to flip"; it is what applies when there is nothing to read.

import type { Booking, Participant, ParticipantUser, Staff } from '../opsBoardTypes';

/** What a colour channel can be driven by. The two channels must differ. */
export type ChipChannelSource = 'kind' | 'instructor' | 'base';

/** A resolved channel assignment — what the chip renderers actually consume. */
export interface ChipChannels { topBar: ChipChannelSource; fill: ChipChannelSource; }

/**
 * The FALLBACK channel assignment, and the shipped defaults. Applies when there is no settings
 * row to read (offline cache miss, failed reference fetch) or when the row cannot be trusted —
 * see `resolveChipChannels`. It must always name two DIFFERENT sources.
 */
export const CHIP_CHANNELS: ChipChannels = {
  topBar: 'kind',
  fill: 'instructor',
};

/**
 * The WIRE spelling of a channel setting — `CompanySettings.chip_top_bar_channel` /
 * `chip_fill_channel`, as the database and the API name them.
 *
 * ⚠⚠ THE WIRE VALUE IS NOT THE RENDER VALUE. The settings column says `'booking_kind'`; the
 * renderer's `ChipChannelSource` says `'kind'`. Passing the settings value straight through
 * WOULD NOT ERROR — DayView's channelColor is an if/else chain whose final arm is the base
 * colour, so an unrecognised source silently means "colour by base". The board would look
 * entirely plausible to anyone who did not already know the scheme. That is why the mapping
 * below exists, why it is the ONLY place the translation happens, and why it is total.
 */
export type ChipChannelSetting = 'booking_kind' | 'instructor' | 'base';

/**
 * The mapping, in one place. Typed `Record<ChipChannelSetting, …>` so it is TOTAL at compile
 * time: adding a member to the setting union fails the build here until it is mapped, rather
 * than falling through to a default at runtime.
 */
const SOURCE_BY_SETTING: Record<ChipChannelSetting, ChipChannelSource> = {
  booking_kind: 'kind',
  instructor:   'instructor',
  base:         'base',
};

/**
 * One wire value → one render source, or null when it is not a value we know.
 *
 * ⚠ The union type is a claim about the wire, NOT a guarantee about it. This runs on whatever
 * the API or a stale offline cache actually handed us, so it is checked at runtime too.
 * `hasOwnProperty` rather than a truthiness test, so `'constructor'` cannot resolve.
 */
function toChannelSource(value: unknown): ChipChannelSource | null {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SOURCE_BY_SETTING, value)
    ? SOURCE_BY_SETTING[value as ChipChannelSetting]
    : null;
}

/**
 * The tenant's channel assignment, resolved from company settings, with CHIP_CHANNELS as the
 * fallback. Call this ONCE per board render and pass the result down; do not translate at the
 * point of use.
 *
 * ⚠ FALLS BACK AS A PAIR, NEVER PER CHANNEL. If one channel is unrecognised, taking the
 * fallback for only that one could pair it with the other's identical value ({kind, kind}) —
 * a chip with one colour doing two jobs, which is the state this guards against.
 *
 * ⚠ EQUAL CHANNELS FALL BACK. The server validates the two differ on write
 * (company-settings.service.ts assertChannelsDiffer); the DATABASE DELIBERATELY DOES NOT, so a
 * row written by any other path can carry two equal channels. Rendering it would waste a
 * channel and make the chip read as a single colour, so the constant applies instead.
 */
export function resolveChipChannels(
  settings?: { chip_top_bar_channel?: string | null; chip_fill_channel?: string | null } | null,
): ChipChannels {
  const topBar = toChannelSource(settings?.chip_top_bar_channel);
  const fill   = toChannelSource(settings?.chip_fill_channel);
  if (topBar === null || fill === null || topBar === fill) return CHIP_CHANNELS;
  return { topBar, fill };
}

/**
 * Fill strength, as a hex alpha suffix. `30` is 48/255 ≈ 19 percent (settled 2026-09-04).
 * The pre-existing kind tint was `22` (34/255 ≈ 13 percent) — see DayView's ordinary branch.
 */
export const FILL_ALPHA = '30';

/** Height of the booking-type bar along the top edge of the chip, in SVG units. */
export const TOP_BAR_H = 4;

/**
 * Derived instructor colours, used when a user has no assigned `ui_color`.
 *
 * ⚠ THESE HUES ARE CHOSEN, NOT ARITHMETIC, AND THE CHOICE IS LOAD-BEARING. Type and instructor
 * now sit on the same chip — one as a solid top bar, one as a 19 percent fill — so a derived
 * colour landing on a common booking-type hue makes the chip read as a single colour. A naive
 * `hash % 360` hue hits that constantly, and an evenly spaced wheel is no better: against this
 * tenant's kind colours the best 8-point wheel lands 6.6 degrees from one of them.
 *
 * This set is the packing that survives BOTH constraints (see verify-chip-channels.ts, which
 * re-derives it against the tenant's live kind colours):
 *   - at least 26 degrees from every SATURATED booking-kind hue;
 *   - at least 35 degrees between palette entries, so the entries differ from each other too.
 *
 * Six entries is what those thresholds allow. A seventh was reachable only by crowding three
 * hues into the yellow-green band, where they are not tellable apart at 19 percent alpha.
 * MORE INSTRUCTORS THAN ENTRIES IS FINE AND EXPECTED: the hash repeats, and the admin-assigned
 * `ui_color` is how two people who collide get pulled apart.
 */
export const INSTRUCTOR_PALETTE: readonly string[] = [
  '#a59627', // hue  54
  '#6aa527', // hue  88
  '#27a58e', // hue 170
  '#272fa5', // hue 236
  '#9027a5', // hue 290
  '#a5276e', // hue 327
];

/**
 * FNV-1a over the user id.
 *
 * ⚠ HASH THE ID, NEVER THE NAME. `preferred_name` is editable and project memory records it
 * holding pilot codes in dev; a person changing their name must not change colour.
 */
export function hashUserId(userId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < userId.length; i++) {
    h ^= userId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** The derived colour for a user id — deterministic, and stable across refreshes. */
export function derivedInstructorColor(userId: string): string {
  return INSTRUCTOR_PALETTE[hashUserId(userId) % INSTRUCTOR_PALETTE.length];
}

/**
 * The instructor colour for one user id: the assigned `ui_color` when there is one, otherwise a
 * derived one. Returns null when the id is not a known staff member.
 *
 * ⚠ NULL IS A REAL ANSWER, not an error. A deactivated instructor still attached to an old
 * booking, or a stale offline row, will not be in the staff array; that chip shows no instructor
 * colour and falls back to the neutral fill. Do not "fix" this by deriving from an unknown id —
 * the point of resolving through `staff` is that the colour tracks a real current person.
 */
export function instructorColor(userId: string | null, staffById: Map<string, Staff>): string | null {
  if (!userId) return null;
  const s = staffById.get(userId);
  if (!s) return null;
  return s.ui_color ?? derivedInstructorColor(userId);
}

// ── Participant resolution ───────────────────────────────────────────────────────────────────
//
// ⚠ RESOLVE PEOPLE FROM `b.participants`, NEVER FROM `b.trainee`. The trainee shape carries only
// { id, full_name, preferred_name } (bookings.service.ts) — there is NO structured name on it, so
// "Surname, Firstname" cannot be built from it. The participants' `user` DOES carry first_name /
// last_name, and it also gives us the instructor and the external_name fallback in the same move.

/** Which side of the booking to show. Staff see the student; the student view will ask for the other. */
export type Counterparty = 'student' | 'instructor';

const STUDENT_CAPACITIES = new Set(['student', 'client']);
const INSTRUCTOR_CAPACITIES = new Set(['instructor', 'external_instructor', 'external_examiner']);

/**
 * The lead participant on one side, in ARRAY ORDER.
 *
 * ⚠ DO NOT SORT THIS. The backend already returns participants
 * `orderBy: [{ rank: 'asc' }, { created_at: 'asc' }]` (bookings.service.ts), which IS the
 * lead-instructor rule: nominated lead by rank, then a stable tiebreak on creation. Taking the
 * first match in the array order satisfies it. Re-sorting client-side on a field the array does
 * not carry (there is no created_at on the frontend Participant type) would break the tiebreak
 * and let a chip change colour between refreshes — the exact failure the rule exists to prevent.
 */
export function leadParticipant(b: Booking, side: Counterparty): Participant | null {
  const wanted = side === 'student' ? STUDENT_CAPACITIES : INSTRUCTOR_CAPACITIES;
  for (const p of b.participants ?? []) if (wanted.has(p.capacity)) return p;
  return null;
}

/**
 * "Surname, Firstname" when the structured name is there, otherwise `full_name` UNCHANGED.
 *
 * ⚠ NEVER SPLIT full_name ON A SPACE to manufacture the format. Two separate populations have no
 * structured name: pre-Phase-3 users (first_name/last_name are nullable with no backfill) and
 * CURRENT users held in an old offline cache whose booking shape predates the fields. `full_name`
 * is NOT NULL, so there is always something to show.
 *
 * ⚠ And do NOT reach for preferred_name or pilot_code here. preferred_name may hold a shortening
 * and has held pilot codes in dev; this row is the person's name.
 */
export function formatPersonName(u: Pick<ParticipantUser, 'full_name' | 'first_name' | 'last_name'> | null): string | null {
  if (!u) return null;
  const last = u.last_name?.trim();
  const first = u.first_name?.trim();
  if (last && first) return `${last}, ${first}`;
  return u.full_name?.trim() || null;
}

/** Just the surname, for the narrow-chip fallback. Null when there is no structured surname. */
export function personSurname(u: Pick<ParticipantUser, 'last_name'> | null): string | null {
  return u?.last_name?.trim() || null;
}

/**
 * The name to put on row two: the lead participant on `side`, formatted.
 *
 * An EXTERNAL participant has no `user` record — it carries `external_name` instead, and that is
 * what renders. Returns null when the booking has nobody on that side.
 */
export function counterpartyName(b: Booking, side: Counterparty = 'student'): string | null {
  const p = leadParticipant(b, side);
  if (!p) return null;
  return formatPersonName(p.user) ?? (p.external_name?.trim() || null);
}

/**
 * The chip's fill colour source value: the lead instructor's colour, or null.
 *
 * An external instructor has no `user_id`, so there is no colour to resolve — the chip shows the
 * name on row two and the neutral fill, which is correct rather than a gap.
 */
export function chipInstructorColor(b: Booking, staffById: Map<string, Staff>): string | null {
  const p = leadParticipant(b, 'instructor');
  return p ? instructorColor(p.user_id, staffById) : null;
}
